import React, { useState, useEffect } from 'react';
import { db1 } from '../firebase/config';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  updateDoc, 
  doc, 
  serverTimestamp,
  increment 
} from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Image, Button, Spinner, Alert, Badge, Row, Col, Modal } from 'react-bootstrap';

const NewsPostPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isArabic, setIsArabic] = useState(true);
  const [hasLiked, setHasLiked] = useState(false);
  const [showCopiedAlert, setShowCopiedAlert] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [modalImage, setModalImage] = useState('');

  // Load custom Arabic fonts
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Black.ttf') format('truetype');
        font-weight: 900;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Bold.ttf') format('truetype');
        font-weight: 700;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Medium.ttf') format('truetype');
        font-weight: 500;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Regular.ttf') format('truetype');
        font-weight: 400;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Light.ttf') format('truetype');
        font-weight: 300;
        font-style: normal;
      }
    `;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // MODIFIED: Always use Arabic font for both languages
  const getFontFamily = (weight = 400) => {
    return `'ITF Qomra Arabic', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif`;
  };

  const formatDate = (date) => {
    if (!date) {
      console.warn('No date provided to formatDate');
      return 'Unknown date';
    }
    
    try {
      let dateObj;
      
      if (date instanceof Date) {
        dateObj = date;
      } else if (date && typeof date.toDate === 'function') {
        dateObj = date.toDate();
      } else if (typeof date === 'string') {
        dateObj = new Date(date);
      } else if (typeof date === 'number') {
        dateObj = new Date(date);
      } else {
        console.warn('Unknown date format:', date);
        return 'Invalid date';
      }

      if (isNaN(dateObj.getTime())) {
        console.warn('Invalid date object:', dateObj);
        return 'Invalid date';
      }

      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      };

      if (isArabic) {
        return dateObj.toLocaleDateString('ar-SA', options);
      } else {
        return dateObj.toLocaleDateString('en-US', options);
      }
    } catch (err) {
      console.error('Error formatting date:', err, 'Original date:', date);
      return 'Date error';
    }
  };

  // Add the fetchNews function that was missing
  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching news with token:', token);
        
        if (!db1) {
          throw new Error('Firebase db1 is not properly initialized.');
        }
        
        const newsQuery = query(
          collection(db1, 'news'),
          where('token', '==', token)
        );
        
        const querySnapshot = await getDocs(newsQuery);
        
        if (querySnapshot.empty) {
          setError(translateText('News not found'));
          setLoading(false);
          return;
        }
        
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        
        let createdAtDate;
        try {
          if (data.createdAt && typeof data.createdAt.toDate === 'function') {
            createdAtDate = data.createdAt.toDate();
          } else if (data.createdAt instanceof Date) {
            createdAtDate = data.createdAt;
          } else if (data.createdAt && typeof data.createdAt === 'string') {
            createdAtDate = new Date(data.createdAt);
          } else if (data.createdAt && typeof data.createdAt === 'number') {
            createdAtDate = new Date(data.createdAt);
          } else {
            createdAtDate = new Date();
          }
        } catch (dateError) {
          console.error('Error processing date:', dateError);
          createdAtDate = new Date();
        }

        if (isNaN(createdAtDate.getTime())) {
          createdAtDate = new Date();
        }
        
        const newsItem = {
          id: doc.id,
          title: data?.title || 'No Title',
          content: data?.content || 'No content available',
          author: data?.author || 'مؤسسة إرضاء الإعلامية',
          category: data?.category || 'Breaking News',
          featuredImage: data?.featuredImage || data?.image || null,
          additionalImages: data?.additionalImages || [],
          token: data?.token || doc.id,
          views: parseInt(data?.views) || 0,
          likes: parseInt(data?.likes) || 0,
          tags: data?.tags || [],
          createdAt: createdAtDate,
          ...data
        };
        
        setNews(newsItem);
        
        // Increment view count
        try {
          await updateDoc(doc.ref, {
            views: increment(1),
            lastViewed: serverTimestamp()
          });
        } catch (viewError) {
          console.error('Error updating view count:', viewError);
        }
        
      } catch (err) {
        console.error('Error fetching news:', err);
        setError(`${translateText('Error loading news')}: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchNews();
    }
  }, [token]);

  // Add the handleLike function
  const handleLike = async () => {
    if (!news || hasLiked) return;

    try {
      await updateDoc(doc(db1, 'news', news.id), {
        likes: increment(1),
        lastLiked: serverTimestamp()
      });

      setNews(prev => ({ ...prev, likes: (prev.likes || 0) + 1 }));
      setHasLiked(true);
      
      // Save to localStorage to prevent multiple likes
      const likedPosts = JSON.parse(localStorage.getItem('likedNews') || '{}');
      likedPosts[news.token] = true;
      localStorage.setItem('likedNews', JSON.stringify(likedPosts));
      
      alert(translateText('Thank you for your like!'));
    } catch (err) {
      console.error('Error updating likes:', err);
      alert('Error updating likes. Please try again.');
    }
  };

  // Add the handleShare function
  const handleShare = async () => {
    try {
      const url = window.location.href;
      await navigator.clipboard.writeText(url);
      setShowCopiedAlert(true);
      setTimeout(() => setShowCopiedAlert(false), 3000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShowCopiedAlert(true);
      setTimeout(() => setShowCopiedAlert(false), 3000);
    }
  };

  // Check if user has already liked this post
  useEffect(() => {
    if (news) {
      const likedPosts = JSON.parse(localStorage.getItem('likedNews') || '{}');
      setHasLiked(!!likedPosts[news.token]);
    }
  }, [news]);

  const translations = {
    'Back to News': 'العودة إلى الأخبار',
    'Published on': 'نشر في',
    'Views': 'مشاهدات',
    'Likes': 'إعجابات',
    'Tags': 'الكلمات الدلالية',
    'Author': 'المؤلف',
    'Category': 'الفئة',
    'Error loading news': 'خطأ في تحميل الخبر',
    'News not found': 'الخبر غير موجود',
    'Like': 'أعجبني',
    'Share': 'مشاركة',
    'Reading Time': 'وقت القراءة',
    'Loading news...': 'جاري تحميل الخبر...',
    'minutes': 'دقائق',
    'You already liked this news': 'لقد أعجبت بهذا الخبر مسبقاً',
    'Thank you for your like!': 'شكراً لإعجابك!',
    'Liked': 'معجب به',
    'Link copied to clipboard!': 'تم نسخ الرابط إلى الحافظة!',
    'Breaking News': 'خبر عاجل',
    'Official Statement': 'بيان رسمي',
    'Field Report': 'تقرير ميداني'
  };

  const translateText = (text) => {
    if (text === news?.content) {
      return text;
    }
    return isArabic ? (translations[text] || text) : text;
  };

  const getCategoryTranslation = (category) => {
    if (!category) return translateText('Breaking News');
    return translations[category] || category;
  };

  // Calculate reading time
  const getReadingTime = (content) => {
    const wordsPerMinute = 200;
    const words = content.split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} ${translateText('minutes')}`;
  };

  // Function to open image modal
  const openImageModal = (imageUrl) => {
    setModalImage(imageUrl);
    setShowImageModal(true);
  };

  const styles = {
    container: {
      background: 'linear-gradient(135deg, #F0F0F0 0%, #FFFFFF 50%, #F0F0F0 100%)',
      padding: '0',
      fontFamily: getFontFamily(),
      direction: isArabic ? 'rtl' : 'ltr',
      position: 'relative',
      overflow: 'hidden',
      fontSize: '16px',
      lineHeight: '1.6',
      minHeight: '100vh',
      color: '#32373B'
    },
    content: {
      maxWidth: '900px',
      margin: '0 auto',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      padding: '3rem 2rem',
      borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
      backdropFilter: 'blur(5px)',
      border: '1px solid #D3BA80',
      marginBottom: '3rem',
      marginTop: '2rem'
    },
    header: {
      textAlign: 'center',
      color: '#32373B',
      padding: '120px 20px 60px',
      background: `linear-gradient(135deg, 
        rgba(211, 186, 128, 0.1) 0%, 
        rgba(211, 186, 128, 0.2) 50%, 
        rgba(255, 255, 255, 0) 100%)`,
      position: 'relative',
      overflow: 'hidden',
      borderBottom: '3px solid #D3BA80'
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: '700',
      marginBottom: '1.5rem',
      color: '#255853',
      textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      letterSpacing: '-0.5px',
      lineHeight: '1.2',
      position: 'relative',
      fontFamily: getFontFamily(700)
    },
    titleAccent: {
      display: 'block',
      width: '80px',
      height: '4px',
      background: 'linear-gradient(90deg, #D3BA80, #E0CFA6)',
      margin: '1rem auto',
      borderRadius: '2px'
    },
    languageToggle: {
      position: 'fixed',
      top: '100px',
      right: isArabic ? 'auto' : '20px',
      left: isArabic ? '20px' : 'auto',
      zIndex: '1000'
    },
    toggleButton: {
      backgroundColor: '#255853',
      color: '#FFFFFF',
      border: 'none',
      borderRadius: '25px',
      padding: '10px 20px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(37, 88, 83, 0.3)',
      fontSize: '0.9rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      fontFamily: getFontFamily(500)
    },
    floatingShapes: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      pointerEvents: 'none'
    },
    shape: {
      position: 'absolute',
      background: 'rgba(211, 186, 128, 0.08)',
      borderRadius: '50%'
    },
    footerSection: {
      backgroundColor: '#255853',
      color: '#FFFFFF',
      padding: '3rem 2rem',
      marginTop: '4rem',
      textAlign: 'center',
      fontFamily: getFontFamily(500)
    },
    authorProfile: {
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      objectFit: 'cover',
      marginRight: '12px',
      border: '1px solid rgba(211, 186, 128, 0.3)'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div className="text-center py-5">
            <Spinner 
              animation="border" 
              style={{ 
                color: '#D3BA80', 
                width: '3rem', 
                height: '3rem' 
              }} 
            />
            <p className="mt-3" style={{ color: '#357E77', fontSize: '1.1rem', fontFamily: getFontFamily(500) }}>
              {translateText('Loading news...')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <Alert variant="light" className="text-center" style={{ 
            border: '1px solid #D3BA80', 
            color: '#32373B',
            fontFamily: getFontFamily(400) 
          }}>
            <div className="d-flex align-items-center justify-content-center mb-3">
              <i className="bi bi-exclamation-triangle-fill me-2" style={{ fontSize: '2rem', color: '#D3BA80' }}></i>
              <h5 className="mb-0" style={{ color: '#32373B', fontFamily: getFontFamily(600) }}>{translateText('Error loading news')}</h5>
            </div>
            <p className="mb-3">{error}</p>
            <Button 
              variant="outline-dark" 
              onClick={() => navigate('/news')}
              style={{ 
                borderColor: '#255853', 
                color: '#255853', 
                marginRight: '10px',
                fontFamily: getFontFamily(500)
              }}
            >
              {translateText('Back to News')}
            </Button>
            <Button 
              variant="outline-dark" 
              onClick={() => window.location.reload()}
              style={{ 
                borderColor: '#255853', 
                color: '#255853',
                fontFamily: getFontFamily(500)
              }}
            >
              Retry Loading
            </Button>
          </Alert>
        </div>
      </div>
    );
  }

  if (!news) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <Alert variant="light" className="text-center" style={{ 
            border: '1px solid #D3BA80', 
            color: '#32373B',
            fontFamily: getFontFamily(400) 
          }}>
            <div className="d-flex align-items-center justify-content-center mb-3">
              <i className="bi bi-exclamation-triangle-fill me-2" style={{ fontSize: '2rem', color: '#D3BA80' }}></i>
              <h5 className="mb-0" style={{ color: '#32373B', fontFamily: getFontFamily(600) }}>{translateText('News not found')}</h5>
            </div>
            <Button 
              variant="outline-dark" 
              onClick={() => navigate('/news')}
              style={{ 
                borderColor: '#255853', 
                color: '#255853',
                fontFamily: getFontFamily(500)
              }}
            >
              {translateText('Back to News')}
            </Button>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.floatingShapes}>
        <div style={{...styles.shape, width: '150px', height: '150px', top: '10%', left: '5%', background: 'rgba(211, 186, 128, 0.05)'}}></div>
        <div style={{...styles.shape, width: '120px', height: '120px', top: '60%', right: '10%', background: 'rgba(53, 126, 119, 0.08)'}}></div>
        <div style={{...styles.shape, width: '80px', height: '80px', bottom: '20%', left: '15%', background: 'rgba(224, 207, 166, 0.06)'}}></div>
      </div>

      <div style={styles.languageToggle}>
        <button 
          onClick={() => setIsArabic(!isArabic)}
          style={styles.toggleButton}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#D3BA80';
            e.target.style.transform = 'translateY(-3px) scale(1.05)';
            e.target.style.boxShadow = '0 8px 20px rgba(211, 186, 128, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#255853';
            e.target.style.transform = 'translateY(0) scale(1)';
            e.target.style.boxShadow = '0 4px 15px rgba(37, 88, 83, 0.3)';
          }}
        >
          {isArabic ? 'ENGLISH' : 'العربية'}
        </button>
      </div>

      <header style={styles.header}>
        <div className="container">
          <Button 
            variant="outline-dark" 
            onClick={() => navigate('/news')}
            style={{ 
              borderColor: '#255853', 
              color: '#255853',
              marginBottom: '2rem',
              borderRadius: '20px',
              fontFamily: getFontFamily(500)
            }}
            className="d-flex align-items-center"
          >
            <i className="bi bi-arrow-right me-2" style={isArabic ? { transform: 'scaleX(-1)' } : {}}></i>
            {translateText('Back to News')}
          </Button>
          
          <Badge 
            style={{ 
              fontSize: '1rem',
              backgroundColor: '#255853',
              color: 'white',
              padding: '8px 16px',
              marginBottom: '1.5rem',
              fontFamily: getFontFamily(500)
            }}
          >
            {getCategoryTranslation(news.category)}
          </Badge>
          
          <h1 style={styles.title}>{news.title}</h1>
          <div style={styles.titleAccent}></div>
          
          <div className="d-flex align-items-center justify-content-center mt-3">
            <img 
              src="/al-majd.png" 
              alt="مؤسسة إرضاء الإعلامية"
              style={styles.authorProfile}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: '#357E77', margin: 0, fontWeight: '600', fontFamily: getFontFamily(600) }}>
                {news.author || 'مؤسسة إرضاء الإعلامية'}
              </p>
              <small style={{ color: '#7f8c8d', fontFamily: getFontFamily(400) }}>
                {translateText('Published on')} {formatDate(news.createdAt)}
              </small>
            </div>
          </div>

          <div className="d-flex justify-content-center gap-4 mt-3" style={{ color: '#7f8c8d', fontFamily: getFontFamily(400) }}>
            <small>
              <i className="bi bi-eye me-1"></i>
              {news.views} {translateText('Views')}
            </small>
            <small>
              <i className="bi bi-hand-thumbs-up me-1"></i>
              {news.likes} {translateText('Likes')}
            </small>
            <small>
              <i className="bi bi-clock me-1"></i>
              {getReadingTime(news.content)} {translateText('Reading Time')}
            </small>
          </div>
        </div>
      </header>

      <div style={styles.content}>
        {showCopiedAlert && (
          <Alert variant="light" style={{ 
            border: '1px solid #D3BA80',
            backgroundColor: 'rgba(255,255,255,0.9)',
            color: '#255853',
            textAlign: 'center',
            fontFamily: getFontFamily(400)
          }}>
            <i className="bi bi-check-circle-fill me-2"></i>
            {translateText('Link copied to clipboard!')}
          </Alert>
        )}

        {news.featuredImage && (
          <div className="text-center mb-4">
            <Image 
              src={news.featuredImage} 
              fluid 
              style={{ 
                borderRadius: '12px',
                maxHeight: '500px',
                objectFit: 'cover',
                cursor: 'pointer',
                border: '1px solid rgba(211, 186, 128, 0.2)'
              }}
              onClick={() => openImageModal(news.featuredImage)}
              alt={news.title}
            />
          </div>
        )}

        <Card style={{ 
          border: '1px solid #E0CFA6',
          borderRadius: '12px',
          backgroundColor: '#ffffff'
        }}>
          <Card.Body style={{ padding: '2rem' }}>
            <div 
              style={{ 
                color: '#32373B',
                lineHeight: '1.8',
                fontSize: '1.1rem',
                fontFamily: getFontFamily(400)
              }}
              dangerouslySetInnerHTML={{ __html: news.content.replace(/\n/g, '<br/>') }}
            />
          </Card.Body>
        </Card>

        {news.additionalImages && news.additionalImages.length > 0 && (
          <div className="mt-4">
            <h4 style={{ 
              color: '#255853', 
              marginBottom: '1rem',
              fontFamily: getFontFamily(600)
            }}>Additional Images</h4>
            <Row>
              {news.additionalImages.map((img, index) => (
                <Col key={index} md={6} lg={4} className="mb-3">
                  <Image 
                    src={img} 
                    fluid 
                    style={{ 
                      borderRadius: '8px',
                      height: '200px',
                      objectFit: 'cover',
                      cursor: 'pointer',
                      border: '1px solid rgba(211, 186, 128, 0.2)'
                    }}
                    onClick={() => openImageModal(img)}
                    alt={`Additional image ${index + 1}`}
                  />
                </Col>
              ))}
            </Row>
          </div>
        )}

        {news.tags && news.tags.length > 0 && (
          <div className="mt-4">
            <h5 style={{ 
              color: '#255853', 
              marginBottom: '1rem',
              fontFamily: getFontFamily(600)
            }}>{translateText('Tags')}:</h5>
            <div className="d-flex flex-wrap gap-2">
              {news.tags.map((tag, index) => (
                <Badge 
                  key={index}
                  style={{ 
                    backgroundColor: 'rgba(53, 126, 119, 0.1)',
                    color: '#255853',
                    padding: '6px 12px',
                    fontSize: '0.9rem',
                    fontFamily: getFontFamily(500)
                  }}
                >
                  #{tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mt-4 pt-4 border-top">
          <Button 
            variant={hasLiked ? "success" : "outline-dark"}
            onClick={handleLike}
            disabled={hasLiked}
            style={{ 
              borderRadius: '20px',
              fontWeight: '500',
              fontFamily: getFontFamily(500),
              ...(hasLiked ? {
                backgroundColor: '#27ae60',
                borderColor: '#27ae60'
              } : {
                color: '#255853',
                borderColor: '#255853'
              })
            }}
          >
            <i className={`bi ${hasLiked ? 'bi-hand-thumbs-up-fill' : 'bi-hand-thumbs-up'} me-2`}></i>
            {hasLiked ? translateText('Liked') : translateText('Like')}
          </Button>

          <Button 
            variant="outline-dark"
            onClick={handleShare}
            style={{ 
              color: '#255853', 
              borderColor: '#255853',
              borderRadius: '20px',
              fontWeight: '500',
              fontFamily: getFontFamily(500)
            }}
          >
            <i className="bi bi-share me-2"></i>
            {translateText('Share')}
          </Button>
        </div>
      </div>

      <Modal show={showImageModal} onHide={() => setShowImageModal(false)} size="lg" centered>
        <Modal.Body className="p-0">
          <Image 
            src={modalImage} 
            fluid 
            style={{ 
              width: '100%',
              borderRadius: '8px'
            }}
            alt="News image"
          />
        </Modal.Body>
      </Modal>

      <footer style={styles.footerSection}>
        <Container>
          <p style={{ margin: 0, fontSize: '1rem' }}>
            © 2024 {isArabic ? 'مؤسسة إرضاء الإعلامية' : 'مؤسسة إرضاء الإعلامية'}. {isArabic ? 'جميع الحقوق محفوظة' : 'All rights reserved'}.
          </p>
        </Container>
      </footer>
    </div>
  );
};

export default NewsPostPage;
